import { type ComputedRef, type Ref, ref, watch } from 'vue'

import { handleCreatorsFilter, normalizeCreators } from '@/lib/adapters/creator.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type {
  CreatorItem,
  CreatorItemFilter,
  CreatorItemRaw,
  UserStatsParams,
} from '@/lib/types/creator.types'
import { useErrorStore } from '@/stores/error.stores'

export const useGetCreators = (
  advertiserIdRef: ComputedRef<number | undefined>,
  filter: Ref<CreatorItemFilter>,
) => {
  const data = ref<CreatorItem[]>([])
  const totalPages = ref(1)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchCreators = async (advertiserId: number) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<{ data: CreatorItemRaw[]; last_page?: number }>(
        `/stores/${advertiserId}/userStats`,
        { params: handleCreatorsFilter(filter.value) },
      )
      data.value = normalizeCreators(response.data.data)
      totalPages.value = response.data.last_page ?? 1
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => [advertiserIdRef.value, { ...filter.value }] as const,
    async () => {
      const advertiserId = advertiserIdRef.value
      if (advertiserId) {
        await fetchCreators(advertiserId)
      }
    },
    { immediate: true },
  )

  return { data, totalPages, isLoading, error }
}

export const buildCreatorsExportUrl = (advertiserId: number, filter: CreatorItemFilter): string => {
  const params = new URLSearchParams()
  const built: UserStatsParams = { ...handleCreatorsFilter(filter), format: 'xlsx' }
  delete built.page
  for (const [key, value] of Object.entries(built)) {
    if (value === undefined) {
      continue
    }
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(`${key}[]`, String(item)))
    } else {
      params.append(key, String(value))
    }
  }
  return `/stores/${advertiserId}/userStats?${params.toString()}`
}
